// TopbarDesktop.js

import React, { useState, useEffect, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  Icons,
} from '../../../../components';

import AuthNavLink from '../AuthNavLink';
import NotificationBellIcon from '../NotificationBellIcon';

import css from './TopbarDesktop.module.css';
import RenderLandingPageSectionHero from '../../../PageBuilder/SectionBuilder/SectionHero/RenderLandingPageSectionHero';
import { getEffectiveLocale } from '../../../../util/genericHelpers';
import LocaleMenuModal from '../../../../components/LocaleMenu/LocaleMenu';
import GuestLanguageSelector, {
  normalizeLocale,
  readLocaleFromLocalStorage,
  writeLocaleToLocalStorage,
} from '../GuestLanguageSelector';

import { useDispatch } from 'react-redux';
import { updateProfile } from '../../../ProfileSettingsPage/ProfileSettingsPage.duck';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../../../util/routes';
import {
  ACTIVE_ROLES,
  getActiveRoleLabel,
  getActiveRoleUpdatePayload,
  getOppositeActiveRole,
  getPostSwitchRoute,
  isActiveRoleHost,
  resolveActiveRole,
} from '../../../../util/activeRole';

const SignupLink = () => {
  return (
    <AuthNavLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </AuthNavLink>
  );
};

const LoginLink = () => {
  return (
    <AuthNavLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </AuthNavLink>
  );
};

const InboxLink = ({ activeRole }) => {
  const isHostMode = isActiveRoleHost(activeRole);
  const tab = isHostMode ? 'sales' : 'orders';
  return (
    <NamedLink className={css.topbarLink} name="InboxPage" params={{ tab }}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
      </span>
    </NamedLink>
  );
};

const ListingsLink = ({ activeRole }) => {
  const isHostMode = isActiveRoleHost(activeRole);
  const routeName = isHostMode ? 'ManageListingsPage' : 'SearchPage';
  return (
    <NamedLink className={css.topbarLink} name={routeName}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.yourListingsLink" />
      </span>
    </NamedLink>
  );
};

const ActiveRolePill = ({ activeRole }) => (
  <span
    className={css.activeRolePill}
    aria-label={`Active role: ${getActiveRoleLabel(activeRole)}`}
  >
    {getActiveRoleLabel(activeRole)}
  </span>
);

/**
 * ProfileMenu language selection:
 * - write to localStorage and notify App.js so it updates instantly
 * - persist to currentUser privateData.selectedLanguage (so it remains after login/new device)
 */
const ProfileMenu = ({
  currentPage,
  currentUser,
  currentUserHasListings,
  onLogout,
  intl,
  onManageDisableScrolling,
}) => {
  const [isLocaleOpen, setIsLocaleOpen] = useState(false);
  const [roleSwitchInProgress, setRoleSwitchInProgress] = useState(false);
  const [roleSwitchError, setRoleSwitchError] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  // Current locale shown in the modal:
  // Prefer localStorage (so UI reflects what the app is using), then user profile fallback.
  const currentLocale = useMemo(() => {
    const stored = readLocaleFromLocalStorage();
    if (stored) return stored;
    const userLocale = currentUser?.id ? normalizeLocale(getEffectiveLocale(currentUser)) : 'en';
    return userLocale || 'en';
  }, [currentUser?.id]);

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const activeRole = resolveActiveRole(currentUser);
  const isHostMode = isActiveRoleHost(activeRole);
  const nextRole = getOppositeActiveRole(activeRole);
  const switchLabelId =
    nextRole === ACTIVE_ROLES.HOST
      ? 'TopbarDesktop.switchToHost'
      : 'TopbarDesktop.switchToCreative';

  const handleSelectLocale = nextLocale => {
    const normalized = normalizeLocale(nextLocale);

    try {
      // IntlFromUser applies the locale immediately and syncs it to the user profile.
      writeLocaleToLocalStorage(normalized);
      setIsLocaleOpen(false);
    } catch (e) {
      console.error('Failed to change locale', e);
      setIsLocaleOpen(false);
    }
  };

  const handleRoleSwitch = async e => {
    e.preventDefault();
    e.stopPropagation();

    if (roleSwitchInProgress || !currentUser?.id) {
      return;
    }

    setRoleSwitchError(false);
    setRoleSwitchInProgress(true);

    try {
      const route = getPostSwitchRoute({
        nextRole,
        currentUser,
        currentUserHasListings,
      });
      const updatedUser = await dispatch(
        updateProfile(getActiveRoleUpdatePayload(currentUser, nextRole))
      );

      if (!updatedUser?.id) {
        throw new Error('Failed to persist active role');
      }

      history.push(createResourceLocatorString(route.name, routeConfiguration, route.params, {}));
    } catch (err) {
      console.error('Failed to switch role', err);
      setRoleSwitchError(true);
    } finally {
      setRoleSwitchInProgress(false);
    }
  };

  const LanguageMenuItem = (
    <MenuItem key="SelectLanguage">
      <InlineTextButton
        rootClassName={classNames(css.menuLink, css.menuButtonLike)}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          setIsLocaleOpen(true);
        }}
      >
        <Icons name="globe" />
        <FormattedMessage id="TopbarDesktop.selectLanguage" defaultMessage="Select Language" />
      </InlineTextButton>

      <LocaleMenuModal
        isOpen={isLocaleOpen}
        onClose={() => setIsLocaleOpen(false)}
        currentLocale={currentLocale}
        onSelectLocale={handleSelectLocale}
        title={intl.formatMessage({ id: 'TopbarDesktop.selectLanguage' })}
        onManageDisableScrolling={onManageDisableScrolling}
      />
    </MenuItem>
  );

  const creativeMenuItems = !isHostMode
    ? [
        <MenuItem key="FavoriteListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('FavoriteListingsPage'))}
            name="FavoriteListingsPage"
          >
            <Icons name="unfavorite" />
            <FormattedMessage id="TopbarDesktop.favoriteListingsPageLink" />
          </NamedLink>
        </MenuItem>,
      ]
    : [];

  const sharedMenuItems = [
    <MenuItem key="ProfileSettingsPage">
      <NamedLink
        className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
        name="ProfileSettingsPage"
      >
        <Icons name="profile" />
        <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
      </NamedLink>
    </MenuItem>,
    <MenuItem key="AccountSettingsPage">
      <NamedLink
        className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
        name="AccountSettingsPage"
      >
        <Icons name="settings" />
        <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
      </NamedLink>
    </MenuItem>,
    LanguageMenuItem,
  ];

  const roleSwitchMenuItem = (
    <MenuItem key="switch-role" className={css.roleSwitchMenuItem}>
      <InlineTextButton
        rootClassName={classNames(css.menuLink, css.roleSwitchButton)}
        onClick={handleRoleSwitch}
        inProgress={roleSwitchInProgress}
        disabled={roleSwitchInProgress}
      >
        <Icons name="switchRole" />
        <FormattedMessage id={switchLabelId} />
      </InlineTextButton>
      {roleSwitchError ? (
        <div className={css.roleSwitchError}>
          <FormattedMessage id="TopbarDesktop.switchRoleFailed" />
        </div>
      ) : null}
    </MenuItem>
  );

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar isTopBar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>

      <MenuContent className={css.profileMenuContent}>
        {[...creativeMenuItems, ...sharedMenuItems, roleSwitchMenuItem]}

        <MenuItem key="logout" className={css.logoutMenuItem}>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <Icons name="logout" />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    currentUser,
    currentPage,
    rootClassName,
    authStateReady,
    currentUserHasListings,
    notificationCount = 0,
    isNotificationsOpen = false,
    intl,
    isAuthenticated,
    onLogout,
    onToggleNotifications,
    onCloseNotifications,
    notificationsPanel,
    showSearchForm,
    showTopbarSearch = false,
    onManageDisableScrolling,
  } = props;

  const [mounted, setMounted] = useState(false);
  const [showSecondDiv, setShowSecondDiv] = useState(false);
  const secondDivRef = useRef(null);
  const notificationsWrapperRef = useRef(null);
  const activeRole = resolveActiveRole(currentUser);

  const isLandingPage =
    currentPage === 'LandingPage' || currentPage === '/' || currentPage === undefined;
  const canToggleTopbarSearch = !!showSearchForm;

  useEffect(() => {
    if (isLandingPage && !showTopbarSearch && showSecondDiv) {
      setShowSecondDiv(false);
    }
  }, [isLandingPage, showSecondDiv, showTopbarSearch]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        canToggleTopbarSearch &&
        secondDivRef.current &&
        !secondDivRef.current.contains(event.target)
      ) {
        setShowSecondDiv(false);
      }
    };

    if (canToggleTopbarSearch && showSecondDiv) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [canToggleTopbarSearch, showSecondDiv]);

  useEffect(() => {
    if (!isNotificationsOpen) {
      return undefined;
    }

    const handleClickOutside = event => {
      if (
        notificationsWrapperRef.current &&
        !notificationsWrapperRef.current.contains(event.target)
      ) {
        onCloseNotifications && onCloseNotifications();
      }
    };

    const handleEsc = event => {
      if (event.key === 'Escape') {
        onCloseNotifications && onCloseNotifications();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isNotificationsOpen, onCloseNotifications]);

  const handleClick = () => {
    if (canToggleTopbarSearch) {
      setShowSecondDiv(true);
    }
  };

  useEffect(() => setMounted(true), []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated && !!currentUser?.id;
  const showGuestUi = authStateReady && !isAuthenticated;
  const classes = classNames(rootClassName || css.root, className);

  const listingsLinkMaybe = authenticatedOnClientSide ? (
    <ListingsLink activeRole={activeRole} />
  ) : null;
  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink activeRole={activeRole} />
  ) : null;

  const activeRolePillMaybe = authenticatedOnClientSide ? (
    <ActiveRolePill activeRole={activeRole} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      onManageDisableScrolling={onManageDisableScrolling}
      currentPage={currentPage}
      currentUser={currentUser}
      currentUserHasListings={currentUserHasListings}
      onLogout={onLogout}
      intl={intl}
    />
  ) : null;

  const signupLinkMaybe = showGuestUi ? <SignupLink /> : null;
  const loginLinkMaybe = showGuestUi ? <LoginLink /> : null;

  const guestLanguageSelectorMaybe = showGuestUi ? (
    <GuestLanguageSelector
      intl={intl}
      onManageDisableScrolling={onManageDisableScrolling}
      buttonClassName={css.languageButton}
    />
  ) : null;

  const compactSearchMaybe = canToggleTopbarSearch ? (
    <div className={css.searchFormWrapper}>
      <div
        className={classNames(css.searchPlaceHolder, css.searchPlaceHolderExpand)}
        onClick={handleClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={intl.formatMessage({
          id: 'TopbarDesktop.search.open',
          defaultMessage: 'Open search',
        })}
        data-testid="topbar-compact-search-trigger"
      >
        <div className={css.searchTypeSec}>
          <span className={css.searchType}>
            {intl.formatMessage({
              id: 'TopbarDesktop.search.keyword',
              defaultMessage: 'Keyword',
            })}
          </span>
          <span className={css.searchType}>
            {intl.formatMessage({
              id: 'TopbarDesktop.search.location',
              defaultMessage: 'Location',
            })}
          </span>
          <span className={css.searchType}>
            {intl.formatMessage({
              id: 'TopbarDesktop.search.date',
              defaultMessage: 'Date',
            })}
          </span>
        </div>
        <span className={css.searchIcon}>
          <Icons name="searchIcon" />
        </span>
      </div>
    </div>
  ) : null;

  const canRenderSearchRow = canToggleTopbarSearch && (!isLandingPage || showTopbarSearch);
  const isSearchRowOpen = canRenderSearchRow && showSecondDiv;
  const expandedSearchMaybe = canRenderSearchRow ? (
    <div
      className={classNames(css.topbarSearchFormWrapper, css.topbarSearchFormWrapperExpand, {
        [css.otherPageSearchFormWrapper]: !isLandingPage,
      })}
      ref={secondDivRef}
    >
      <RenderLandingPageSectionHero showSecondDiv={true} isTopbarSearch={true} />
    </div>
  ) : null;

  const showCompactSearch = !!compactSearchMaybe && canRenderSearchRow && !isSearchRowOpen;

  const notificationBadgeText = notificationCount > 99 ? '99+' : `${notificationCount}`;
  const notificationsLabel = intl.formatMessage({
    id: 'Notifications.title',
    defaultMessage: 'Notifications',
  });

  const notificationsTriggerMaybe = authenticatedOnClientSide ? (
    <div className={css.notificationsWrapper} ref={notificationsWrapperRef}>
      <button
        type="button"
        className={css.notificationsButton}
        onClick={onToggleNotifications}
        aria-label={notificationsLabel}
        data-testid="notifications-bell-desktop"
      >
        <NotificationBellIcon className={css.notificationBellIcon} />
        {notificationCount > 0 ? (
          <span className={css.notificationsBadge}>{notificationBadgeText}</span>
        ) : null}
      </button>

      {isNotificationsOpen ? (
        <div className={css.notificationsPopover} role="dialog" aria-label={notificationsLabel}>
          {notificationsPanel}
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div className={classes}>
      <nav className={css.navRow} data-testid="topbar-desktop-nav-row">
        <div className={css.leftSection} data-testid="topbar-logo-section">
          <LinkedLogo
            className={css.logoLink}
            layout="desktop"
            alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
            linkToExternalSite={config?.topbar?.logoLink}
          />
        </div>

        <div className={css.centerSection} data-testid="topbar-search-section">
          {compactSearchMaybe ? (
            <div
              className={classNames(
                css.compactSearchInNav,
                showCompactSearch ? css.compactSearchInNavVisible : css.compactSearchInNavHidden
              )}
              data-testid="topbar-compact-search"
            >
              {compactSearchMaybe}
            </div>
          ) : null}
        </div>

        <div className={css.rightLinks} data-testid="topbar-actions">
          {listingsLinkMaybe}
          {inboxLinkMaybe}
          {notificationsTriggerMaybe}
          {activeRolePillMaybe}
          {profileMenuMaybe}
          {signupLinkMaybe}
          {loginLinkMaybe}
          {guestLanguageSelectorMaybe}
        </div>
      </nav>

      {canRenderSearchRow ? (
        <div
          className={classNames(
            css.searchRow,
            isSearchRowOpen ? css.searchRowOpen : css.searchRowClosed
          )}
        >
          <div className={css.searchRowInner}>{expandedSearchMaybe}</div>
        </div>
      ) : null}
    </div>
  );
};

export default TopbarDesktop;
